import { useEffect, useRef, useState } from 'react';

import { useSignIn } from '@clerk/clerk-react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

import type { FormEvent } from 'react';

import { ErrorAlert } from '~/components/common/ErrorAlert';
import { GitHubLogo, GoogleLogo } from '~/components/common/icons';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Field, FieldError, FieldLabel } from '~/components/ui/field';
import { Input } from '~/components/ui/input';
import { Spinner } from '~/components/ui/spinner';
import { useAttributionCapture } from '~/hooks/useAttribution';
import { extractClerkError } from '~/utils/apiErrors';
import { extractClerkErrorCode, getSignInReasonMessage } from '~/utils/clerkErrorHandler';

type LoadingState = 'idle' | 'google' | 'github' | 'email';

const RESEND_COOLDOWN_SECONDS = 60;

// CustomSignIn renders only inside the /sign-in route, so it reads that route's
// validated search params.
const routeApi = getRouteApi('/sign-in');

export function CustomSignIn() {
  // Capture attribution data on mount
  useAttributionCapture();

  const { signIn, isLoaded } = useSignIn();
  const navigate = useNavigate();
  const { redirect_url, returnUrl, reason } = routeApi.useSearch();
  const [error, setError] = useState<string>('');
  const [errorSource, setErrorSource] = useState<LoadingState>('idle');
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [email, setEmail] = useState<string>('');
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get redirect URL from query params (set by RedirectToSignIn or returnUrl)
  // This preserves the intended destination through OAuth/magic link flows
  const redirectUrl = redirect_url || returnUrl;

  // Banner copy for a sign-in failure that redirected back here with `?reason=`
  // (e.g. from SsoCallbackPage). Local OAuth errors take precedence below.
  const signInReasonMessage = getSignInReasonMessage(reason);

  const handleGoogleSignIn = async () => {
    if (!isLoaded || !signIn) return;

    setError('');
    setErrorSource('idle');
    setLoadingState('google');

    try {
      // Build callback URL with redirect_url param to preserve intended destination
      const callbackUrl = new URL('/sso-callback', window.location.origin);
      if (redirectUrl) {
        callbackUrl.searchParams.set('redirect_url', redirectUrl);
      }

      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: callbackUrl.toString(),
        redirectUrlComplete: callbackUrl.toString(),
      });
    } catch (err) {
      const clerkErrorResult = extractClerkErrorCode(err);
      if (clerkErrorResult.shouldRedirect) {
        void navigate({
          to: '/auth-error',
          search: { type: clerkErrorResult.scenario },
          replace: true,
        });
        return;
      }
      const errorMessage = extractClerkError(err, 'Failed to sign in with Google');
      setError(errorMessage);
      setErrorSource('google');
      setLoadingState('idle');
    }
  };

  const handleGitHubSignIn = async () => {
    if (!isLoaded || !signIn) return;

    setError('');
    setErrorSource('idle');
    setLoadingState('github');

    try {
      // Build callback URL with redirect_url param to preserve intended destination
      const callbackUrl = new URL('/sso-callback', window.location.origin);
      if (redirectUrl) {
        callbackUrl.searchParams.set('redirect_url', redirectUrl);
      }

      await signIn.authenticateWithRedirect({
        strategy: 'oauth_github',
        redirectUrl: callbackUrl.toString(),
        redirectUrlComplete: callbackUrl.toString(),
      });
    } catch (err) {
      const clerkErrorResult = extractClerkErrorCode(err);
      if (clerkErrorResult.shouldRedirect) {
        void navigate({
          to: '/auth-error',
          search: { type: clerkErrorResult.scenario },
          replace: true,
        });
        return;
      }
      const errorMessage = extractClerkError(err, 'Failed to sign in with GitHub');
      setError(errorMessage);
      setErrorSource('github');
      setLoadingState('idle');
    }
  };

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!signIn || !isLoaded || !email.trim()) return;

    setError('');
    setErrorSource('idle');
    setLoadingState('email');

    try {
      // Build callback URL with redirect_url param to preserve intended destination
      const callbackUrl = new URL('/magic-link-callback', window.location.origin);
      if (redirectUrl) {
        callbackUrl.searchParams.set('redirect_url', redirectUrl);
      }

      const result = await signIn.create({
        strategy: 'email_link',
        identifier: email,
        redirectUrl: callbackUrl.toString(),
      });

      if (result.status === 'needs_first_factor') {
        setEmailSent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setError('Unable to send magic link. Please try again.');
        setErrorSource('email');
      }
    } catch (err) {
      const clerkErrorResult = extractClerkErrorCode(err);
      if (clerkErrorResult.shouldRedirect) {
        void navigate({
          to: '/auth-error',
          search: { type: clerkErrorResult.scenario },
          replace: true,
        });
        return;
      }
      const errorMessage = extractClerkError(err, 'Failed to send magic link');
      setError(errorMessage);
      setErrorSource('email');
    } finally {
      setLoadingState('idle');
    }
  };

  // Tick the resend cooldown down to zero once per second while it's active.
  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
      return;
    }

    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
    // oxlint-disable-next-line react/exhaustive-deps -- interval re-arms itself via setResendCooldown
  }, [resendCooldown > 0]);

  const handleResend = async () => {
    if (!signIn || !isLoaded || !email.trim() || resendCooldown > 0) return;

    setError('');
    setLoadingState('email');

    try {
      const callbackUrl = new URL('/magic-link-callback', window.location.origin);
      if (redirectUrl) {
        callbackUrl.searchParams.set('redirect_url', redirectUrl);
      }

      const result = await signIn.create({
        strategy: 'email_link',
        identifier: email,
        redirectUrl: callbackUrl.toString(),
      });

      if (result.status === 'needs_first_factor') {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setError('Unable to resend magic link. Please try again.');
      }
    } catch (err) {
      const clerkErrorResult = extractClerkErrorCode(err);
      if (clerkErrorResult.shouldRedirect) {
        void navigate({
          to: '/auth-error',
          search: { type: clerkErrorResult.scenario },
          replace: true,
        });
        return;
      }
      const errorMessage = extractClerkError(err, 'Failed to resend magic link');
      setError(errorMessage);
    } finally {
      setLoadingState('idle');
    }
  };

  const handleTryAgain = () => {
    setEmailSent(false);
    // Keep the entered email so the user doesn't have to retype it when
    // switching to another sign-in method.
    setError('');
    setErrorSource('idle');
  };

  const isLoading = loadingState !== 'idle';

  // Success state: email sent confirmation
  if (emailSent) {
    return (
      <div className="flex flex-col gap-6">
        {/* Informational, not an error - no alert role */}
        <Alert variant="success" className="text-left">
          <div>
            <p className="text-sm font-medium">Check your email for a sign-in link</p>
            <p className="mt-1 text-sm opacity-90">
              We sent a magic link to <strong>{email}</strong>. The link expires in 15 minutes - be
              sure to check your spam folder if it doesn't arrive shortly.
            </p>
          </div>
        </Alert>

        {error && <ErrorAlert message={error} />}

        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => void handleResend()}
          disabled={isLoading || resendCooldown > 0}
          aria-busy={isLoading}
        >
          {isLoading && <Spinner data-icon="inline-start" aria-hidden="true" />}
          {resendCooldown > 0 ? `Resend link (${resendCooldown}s)` : 'Resend link'}
        </Button>

        <Button variant="ghost" size="lg" className="w-full" onClick={handleTryAgain}>
          Try a different method
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Error Alert - email errors surface inline on the Field instead. A local
          OAuth error always takes precedence over the `?reason=` banner so the
          two never stack. */}
      {error && errorSource !== 'email' ? (
        <ErrorAlert message={error} />
      ) : (
        signInReasonMessage && <ErrorAlert title={null} message={signInReasonMessage} />
      )}

      {/* OAuth Buttons */}
      <div className="flex flex-col gap-4">
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => void handleGoogleSignIn()}
          disabled={isLoading || !isLoaded}
          aria-busy={loadingState === 'google'}
        >
          {loadingState === 'google' ? (
            <Spinner
              data-icon="inline-start"
              data-testid="signin-google-spinner"
              aria-hidden="true"
            />
          ) : (
            <GoogleLogo />
          )}
          Sign in with Google
        </Button>

        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => void handleGitHubSignIn()}
          disabled={isLoading || !isLoaded}
          aria-busy={loadingState === 'github'}
        >
          {loadingState === 'github' ? (
            <Spinner
              data-icon="inline-start"
              data-testid="signin-github-spinner"
              aria-hidden="true"
            />
          ) : (
            <GitHubLogo />
          )}
          Sign in with GitHub
        </Button>
      </div>

      {/* Divider */}
      <div className="my-2 flex items-center gap-2">
        <div className="h-px flex-1 bg-border" />
        <span className="px-2 font-mono text-[11px] font-medium tracking-[0.05em] text-muted-foreground uppercase">
          or
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Email Magic Link Form */}
      <form onSubmit={(e) => void handleEmailSubmit(e)} className="flex flex-col gap-4">
        <Field data-invalid={errorSource === 'email' ? true : undefined}>
          <FieldLabel htmlFor="signin-email">Email address</FieldLabel>
          <Input
            id="signin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            autoComplete="email"
            placeholder="you@example.com"
            aria-invalid={errorSource === 'email' ? true : undefined}
          />
          {errorSource === 'email' && <FieldError>{error}</FieldError>}
        </Field>

        <Button
          type="submit"
          variant="outline"
          size="lg"
          className="w-full"
          disabled={isLoading || !isLoaded || !email.trim()}
          aria-busy={loadingState === 'email'}
        >
          {loadingState === 'email' && (
            <Spinner
              data-icon="inline-start"
              data-testid="signin-email-spinner"
              aria-hidden="true"
            />
          )}
          Send me a link
        </Button>
      </form>
    </div>
  );
}
